import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngxs/store';
import { isAfterDate } from '@osos/shared/legacy-common';
import {
  AbstractSecurityStorage,
  EventTypes,
  OidcSecurityService,
  PublicEventsService,
} from 'angular-auth-oidc-client';
import {
  defer,
  EMPTY,
  Observable,
  of,
  repeat,
  ReplaySubject,
  share,
  timer,
} from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { ConfigService } from '../../config';
import { AuthState, LanguageState } from '../../state';
import { AppStorageService } from '../app-storage/app-storage.service';
import { AuthServiceBase } from './auth-service-base.model';
// TODO: Replace the use of this const with the `checkSessionChange` event, if it's supported from server side.
const SESSION_EXPIRES_IN = 14 * 24 * 60 * 60 * 1000; // 14 days in miliseconds

/** An offset to renew the anonymous token before it expires. */
const ANONYMOUS_TOKEN_CACHE_TIMEOUT = 5 * 60 * 1000; // 5 mins in miliseconds
const RETURN_URL_KEY = 'auth:return-url'; // <== added

@Injectable({
  providedIn: 'root',
})
export class AuthService implements AuthServiceBase {
  private store = inject(Store);
  private oidcSecurityService = inject(OidcSecurityService);
  private oidcSecurityStorage = inject(AbstractSecurityStorage);
  private oidcEventsService = inject(PublicEventsService);
  private appStorageService = inject(AppStorageService);
  private configService = inject(ConfigService);
  private http = inject(HttpClient);

  private _isAuthenticated: boolean;
  private _userLocalSessionExpired: boolean;
  private _tokenSource$: Observable<string>;

  constructor() {
    this.store.select(AuthState.isAuthenticated).subscribe((isAuth = false) => {
      if (this._isAuthenticated !== isAuth) {
        this._isAuthenticated = isAuth;
        this._determineAccessTokenSource();
      }
    });

    this.oidcEventsService
      .registerForEvents()
      .pipe(
        filter((event) => event.type === EventTypes.NewAuthenticationResult),
        withLatestFrom(this.oidcSecurityService.isAuthenticated$),
      )
      .subscribe(([, { isAuthenticated }]) => {
        if (isAuthenticated) {
          this._updateSessionExpirationTime();
        }
      });
  }

  /** Checks the server for an authenticated session using the iframe silent renew if not locally authenticated. */
  checkAuth(): Observable<boolean> {
    return this.oidcSecurityService.getAuthenticationResult().pipe(
      switchMap((authResult) => {
        if (authResult) {
          return this._resetAuthDataIfSessionStaleOrStuck(authResult);
        }
        return of(true);
      }),
      switchMap(() =>
        this.oidcSecurityService.checkAuthIncludingServer().pipe(
          map((authResult) => authResult?.isAuthenticated ?? false),
          catchError(() => {
            // console.error('An error occurred during server auth check:', error);
            return of(false); // Return false to indicate the user is not authenticated
          }),
        ),
      ),
      tap((isAuthenticated) => {
        this._userLocalSessionExpired = !isAuthenticated;
      }),
      catchError(() => {
        // console.error('An error occurred:', error);
        // Handle the error here. For example, you might want to dispatch an action:
        // dispatch(new AuthErrorAction(error));
        // Return an Observable that the rest of the pipe can subscribe to:
        return of(false); // Return false to indicate the user is not authenticated
      }),
    );
  }

  /** Redirects the user to the Security Token Service to begin the authentication process. */
  signIn(): void {
    this.store
      .select(LanguageState.currentLanguage)
      .pipe(
        filter((lang) => !!lang?.languageName),
        take(1),
      )
      .subscribe((lang) => {
        const currentUrl = window.location.href.replace(
          this.configService.configuration.APP_URL,
          '',
        );
        if (
          currentUrl &&
          currentUrl !== '/' &&
          currentUrl !== '' &&
          !currentUrl.startsWith('/403')
        ) {
          this.appStorageService.write(RETURN_URL_KEY, currentUrl);
        }

        this.oidcSecurityService.authorize(undefined, {
          customParams: {
            lang: lang.languageName,
          },
        });
      });
  }

  /**
   * Revokes the refresh token (if present) and the access token on the server and then performs the logoff operation.
   * The refresh token and and the access token are revoked on the server.
   * If the refresh token does not exist only the access token is revoked. Then the logout run.
   */
  signOut(): Observable<any> {
    // If the user is not logged out yet, then, perform the logout on the server,
    // Otherwise, perform the local logout.
    // A workaround to resolve logging the user out on the other tabs if the user is already logged out.
    // Related to #1396 issue on angular-auth-oidc-client lib:
    // https://github.com/damienbod/angular-auth-oidc-client/issues/1396
    return this.oidcSecurityService.getAccessToken().pipe(
      switchMap((token) => {
        if (token) {
          return this.oidcSecurityService.logoffAndRevokeTokens();
        } else {
          this.oidcSecurityService.logoffLocal();
          return of(true);
        }
      }),
      tap(() => {
        this._updateLastLogoutTime();
      }),
      catchError(() => {
        // console.error('An error occurred:', error);
        // Handle the error here. For example, you might want to return an Observable of a default value:
        return of(null);
      }),
    );
  }

  /** Reset auth data from in storage. */
  resetAuthData(): void {
    this.oidcSecurityService.logoffLocal();
    this._userLocalSessionExpired = true;
  }

  /** Returns the access token. */
  getAccessToken(): Observable<string> {
    return this._tokenSource$;
  }

  /** Returns the Id of current logged in user. */
  getCurrentUserId(): Observable<string> {
    return this.oidcSecurityService.getUserData().pipe(
      map((userData) => userData?.sub),
      catchError(() => {
        // console.error('An error occurred:', error);
        // Handle the error here. For example, you might want to return an Observable of a default value:
        return of(null);
      }),
    );
  }

  /** Checks whether the session is expired. */
  isUserSessionExpired(): boolean {
    if (this._userLocalSessionExpired) {
      return true;
    }

    const exp = this.appStorageService.read('session-exp');
    const now = new Date(new Date().toUTCString()).valueOf();

    return !exp || isAfterDate(now, Number(exp));
  }

  private _determineAccessTokenSource(): void {
    if (this._isAuthenticated) {
      this._tokenSource$ = this._getAccessTokenSourceForAuthUser();
    } else {
      this._tokenSource$ = this._getAccessTokenSourceForAnonymousUser();
    }
  }

  private _getAccessTokenSourceForAuthUser(): Observable<string> {
    return this.oidcSecurityService.getAccessToken().pipe(
      mergeMap((token) => {
        // If the token is removed but the user session is not expired yet, attempt to restore it from the server:
        if (!token && !this._userLocalSessionExpired) {
          console.debug(
            '[DEBUG] Attempt to restore the token from the server if an unexpected error caused it to be removed',
          );
          return this.oidcSecurityService.checkAuthIncludingServer();
        } else {
          return of({ accessToken: token });
        }
      }),
      map((authResult) => authResult?.accessToken),
      catchError(() => {
        // console.error('An error occurred:', error);
        // Handle the error here. For example, you might want to return an Observable of a default value:
        return of(null);
      }),
      repeat({
        delay: () => (!this._userLocalSessionExpired ? timer(2000) : EMPTY),
        count: 10,
      }),
      filter((token) => !!token),
      take(1),
    );
  }

  /** The access-token to be used for anonymous users.
   * For authenticated users, directly should use the `OidcSecurityService.getAccessToken()` to retrieve the fresh token from storage.
   */
  private _getAccessTokenSourceForAnonymousUser(): Observable<string> {
    return defer(() => this._getClientToken()).pipe(
      tap(() => console.debug('[DEBUG] Get new anonymous token')),
      share({
        connector: () => new ReplaySubject(1),
        resetOnComplete: () => timer(ANONYMOUS_TOKEN_CACHE_TIMEOUT),
      }),
    );
  }

  /** Load the client token that will be used for anonymous access */
  private _getClientToken(): Observable<string> {
    const { SYSTEM_ClIENT_ID, SYSTEM_ClIENT_SECRET } =
      this.configService.configuration;

    const params = new HttpParams()
      .set('client_id', SYSTEM_ClIENT_ID)
      .set('client_secret', SYSTEM_ClIENT_SECRET)
      .set('grant_type', 'client_credentials');

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });

    return this.oidcSecurityService.getConfiguration().pipe(
      switchMap(({ authWellknownEndpoints }) =>
        this.http.post(authWellknownEndpoints.tokenEndpoint, params, {
          headers,
        }),
      ),
      map((res: { access_token: string }) => res.access_token),
      catchError(() => {
        // console.error('An error occurred:', error);
        // Handle the error here. For example, you might want to return an Observable of a default value:
        return of(null);
      }),
    );
  }

  private _resetAuthDataIfSessionStaleOrStuck(authResult: any) {
    return this._getStoredSessionData().pipe(
      tap(({ silentRenewRunning, accessTokenExpiresAt }) => {
        // Case 1: The silent renew process is stuck
        // If the silent renew process was running before calling `checkAuthIncludingServer`, it's likely stuck.
        // This situation can cause the app to hang if the user refreshes the tab or window while the process is still ongoing.
        // To avoid blocking the app initializer, we reset the session.

        // Case 2: The token has been issued before the last logout
        // It's possible for a token to be issued before the last logout if the user logged out from a different tab or window.
        // This leads to an invalid access token being used, putting the app into an invalid state until the session is refreshed.
        // To prevent this, we reset the session if the token was issued before the last logout.
        if (
          silentRenewRunning ||
          this._isSessionStale(accessTokenExpiresAt, authResult.expires_in)
        ) {
          console.debug(
            '[DEBUG] The user session is epxired, and it will be reset.',
          );
          this.resetAuthData();
        }
      }),
    );
  }

  private _isSessionStale(accessTokenExpiresAt: number, expiresIn: number) {
    const lastLogoutAt = this.appStorageService.read('logout-at');
    const tokenLifetimeInMilliseconds = Number(expiresIn) * 1000;
    const accessTokenIssuedAt = new Date(
      accessTokenExpiresAt - tokenLifetimeInMilliseconds,
    );

    // Checking whether the token was issued after the last logout
    return (
      !!lastLogoutAt && isAfterDate(Number(lastLogoutAt), accessTokenIssuedAt)
    );
  }

  private _getStoredSessionData(): Observable<{
    silentRenewRunning: any;
    accessTokenExpiresAt: number;
  }> {
    return this.oidcSecurityService.getConfiguration().pipe(
      map(({ configId }) => {
        const storedConfig = this.oidcSecurityStorage.read(configId);

        if (!storedConfig) {
          return null;
        }

        // From oidc lib's StorageKeys
        const {
          storageSilentRenewRunning: silentRenewRunning,
          access_token_expires_at: accessTokenExpiresAt,
        } = JSON.parse(storedConfig) ?? {};

        return { silentRenewRunning, accessTokenExpiresAt };
      }),
      catchError(() => {
        // console.error('An error occurred:', error);
        // Handle the error here. For example, you might want to return an Observable of a default value:
        return of(null);
      }),
    );
  }

  private _updateSessionExpirationTime(): void {
    // Store the session expiration time value in the app storage:
    const exp =
      new Date(new Date().toUTCString()).valueOf() + SESSION_EXPIRES_IN;
    this.appStorageService.write('session-exp', exp);
  }

  private _updateLastLogoutTime(): void {
    // Store the logout date-time in the app storage:
    const lastLogout = new Date(new Date().toUTCString()).valueOf();
    this.appStorageService.write('logout-at', lastLogout);
  }
}
